import React, { useEffect, useState } from "react";
import { fetchSessions as fetchSessionsApi } from "@/lib/widget-session";
import {
  ArrowLeft,
  MessageSquare,
  ChevronRight,
  Home,
} from "lucide-react";
import { getMessageDisplayText, getMessageHeader } from "@/utils/message-parser";

interface ChatSession {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  contact_id: string | null;
  messages: any[];
  agent_id: string | null;
}

interface ChatSessionsListProps {
  organizationId: string;
  contactId: string | null;
  onBack: () => void;
  onOpenChat: () => void;
  theme: "light" | "dark";
  customColors?: {
    backgroundColor?: string;
    textColor?: string;
    secondaryTextColor?: string;
    borderColor?: string;
    buttonBackgroundColor?: string;
  };
}

const ChatSessionsList: React.FC<ChatSessionsListProps> = ({
  organizationId,
  contactId,
  onBack,
  onOpenChat,
  theme,
  customColors,
}) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);

  const primaryColor = customColors?.buttonBackgroundColor || "#2563eb";

  useEffect(() => {
    const fetchSessions = async () => {
      if (!contactId) {
        setLoading(false);
        return;
      }

      try {
        // Token-authenticated read (api/messages?view=sessions) — the widget
        // never queries private tables with the anon key.
        const sessionsData = await fetchSessionsApi(organizationId, contactId);
        if (sessionsData === null) throw new Error("sessions fetch failed");

        const processedSessions =
          sessionsData?.map((session) => ({
            ...session,
            messages: Array.isArray(session.messages) ? session.messages : [],
          })) || [];

        setSessions(processedSessions);
      } catch (error) {
        console.error("Error fetching chat sessions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [organizationId, contactId]);

  // Helper function to format time ago
  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const messageDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else if (diffInMinutes < 10080) { // Less than a week
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    } else {
      return `${Math.floor(diffInMinutes / 10080)}w ago`;
    }
  };

  const getLastMessage = (session: ChatSession) => {
    if (!session.messages || session.messages.length === 0) {
      return { text: "No messages", sender: "System", isFromAgent: false };
    }

    // Get the last message (could be from user or agent)
    const lastMessage = session.messages[session.messages.length - 1];
    const rawMessageText = lastMessage.content || lastMessage.text || "No content";
    
    // Use parser utility to extract header or body
    const header = getMessageHeader(rawMessageText);
    const messageText = header || getMessageDisplayText(rawMessageText);
    
    // Determine sender
    const isFromAgent = lastMessage.sender_type === 'human-web' || lastMessage.sender_type === 'bot-web';
    const senderName = lastMessage.sender_type === 'human-web' ? 'Human' : 'AI Agent';
    
    return {
      text: messageText.length > 50 ? messageText.substring(0, 50) + "..." : messageText,
      sender: isFromAgent ? senderName : 'You',
      isFromAgent,
      createdAt: lastMessage.created_at
    };
  };

  return (
    <div className="flex flex-col h-full bg-white relative">
      {/* Header */}
      <div className="flex items-center justify-center py-4 border-b border-gray-100 relative">
        <button
          onClick={onBack}
          className="absolute left-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
      </div>

      {/* Messages List */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Loading conversations...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">No conversations yet</p>
          </div>
        ) : (
          sessions.map((session, index) => {
            const lastMessage = getLastMessage(session);
            return (
              <button
                key={session.id}
                onClick={onOpenChat}
                className="w-full py-4 px-6 flex items-center gap-4 hover:bg-gray-50 transition-colors border-b border-gray-50 text-left"
              >
                {/* Avatar */}
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-lg">
                    {lastMessage.isFromAgent ? lastMessage.sender.charAt(0) : 'Y'}
                  </span>
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 font-medium truncate">
                    {lastMessage.text}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {lastMessage.sender} • {getTimeAgo(lastMessage.createdAt || session.updated_at)}
                  </p>
                </div>

                {/* Chevron */}
                <ChevronRight className="text-gray-400 w-5 h-5 flex-shrink-0" />
              </button>
            );
          })
        )}
      </div>

      

             {/* Bottom Navigation */}
       <div className="flex items-center justify-around py-4 bg-white border-t border-gray-100">
         <button
           onClick={onBack}
           className="flex flex-col items-center gap-1 py-2 px-4"
         >
           <Home className="w-6 h-6 text-gray-400" />
           <span className="text-gray-400 text-sm">Home</span>
         </button>
        <button
          onClick={onOpenChat}
          className="flex flex-col items-center gap-1 py-2 px-4"
        >
          <MessageSquare className="w-6 h-6 text-gray-400" />
          <span className="text-gray-400 text-sm">Open chat</span>
        </button>
       </div>
    </div>
  );
};

export default ChatSessionsList;
